import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import loader from "../../../../components/recharts/loader.gif";
import { ReactComponent as Edit } from "../../../../icons/info-Simulator.svg";
import { normalizeDisplayLabel } from "../../../../utility/displayText";
import { fetchModSimulationTopBuyer } from "../../Utility/RestApi";
interface ContributorData {
  time_slot: number;
  generator: string;
  quantum: number;
  price: number;
}

export default function SimulationContributorAnylysis({
  refreshKey,
  selectedBlock,
  selectedGeneratorName,
  setError,
}: {
  refreshKey: number;
  selectedBlock: any[];
  selectedGeneratorName: any[];
  setError?: any;
}) {
  const [data, setData] = useState<ContributorData[]>([]);
  const [loading, setLoading] = useState(false);
  const preSelectedDate = useSelector(
    (state: any) => state.modGridcoDate.selectedDate,
  );
  const modSelectedSession = useSelector(
    (state: any) => state.modGridcoSession.modSession,
  );
  const [filtered, setfiltered] = useState<ContributorData[]>([]);

  const priceMaxPrice = Math.max(...filtered.map((d) => d.price));
  const quantumMaxPrice = Math.max(...filtered.map((d) => d.quantum));

  const handleFetchData = useCallback(async () => {
    if (!preSelectedDate || !modSelectedSession) return;

    try {
      setLoading(true);
      const res = await fetchModSimulationTopBuyer(
        preSelectedDate,
        modSelectedSession,
      );

      setData(res.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("An error occurred while fetching data.");
    } finally {
      setLoading(false);
    }
  }, [modSelectedSession, preSelectedDate, setError]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData, refreshKey]);

  useEffect(() => {
    let filteredData = data;

    // Apply generator filter first
    if (selectedGeneratorName.length > 0) {
      const generator = selectedGeneratorName.map((o: any) => o.value ?? o);
      filteredData = filteredData.filter((row) =>
        generator.includes(row.generator),
      );
    }

    // Apply block filter
    if (selectedBlock.length > 0) {
      const blocks = selectedBlock.map((b: any) => Number(b.value ?? b));
      filteredData = filteredData.filter((row) =>
        blocks.includes(Number(row.time_slot)),
      );
    }

    if (selectedBlock.length === 1) {
      // Single block selected: show individual generators with quantum/4
      const convertedData = filteredData.map((item) => ({
        ...item,
        quantum: item.quantum / 4,
      }));
      setfiltered(convertedData);
    } else if (selectedBlock.length > 1) {
      // Multiple blocks selected: group by generator and sum, then divide by 4
      const groupedData: { [key: string]: ContributorData[] } = {};
      filteredData.forEach((item) => {
        if (!groupedData[item.generator]) {
          groupedData[item.generator] = [];
        }
        groupedData[item.generator].push(item);
      });

      const aggregatedData: ContributorData[] = Object.keys(groupedData).map(
        (generatorName) => {
          const generatorGroup = groupedData[generatorName];

          const sumQuantum = generatorGroup.reduce(
            (sum, item) => sum + item.quantum,
            0,
          );
          const sumPrice = generatorGroup.reduce(
            (sum, item) => sum + item.price,
            0,
          );

          return {
            ...generatorGroup[0],
            quantum: sumQuantum / 4,
            price: sumPrice,
          };
        },
      );

      setfiltered(aggregatedData);
    } else {
      // No blocks selected: group by generator name and calculate sum
      const groupedData: { [key: string]: ContributorData[] } = {};
      filteredData.forEach((item) => {
        if (!groupedData[item.generator]) {
          groupedData[item.generator] = [];
        }
        groupedData[item.generator].push(item);
      });

      const aggregatedData: ContributorData[] = Object.keys(groupedData).map(
        (generatorName) => {
          const generatorGroup = groupedData[generatorName];

          const sumQuantum = generatorGroup.reduce(
            (sum, item) => sum + item.quantum,
            0,
          );
          const sumPrice = generatorGroup.reduce(
            (sum, item) => sum + item.price,
            0,
          );

          return {
            ...generatorGroup[0],
            quantum: sumQuantum / 4,
            price: sumPrice,
          };
        },
      );

      setfiltered(aggregatedData);
    }
  }, [data, selectedBlock, selectedGeneratorName]);

  return (
    <>
      <div className="dash-card col-span-1 md:col-span-12 border border-[#DFEAF2] !bg-[#fff] !shadow-[rgba(149,157,165,0.2)_0px_8px_24px] !min-h-[100%] w-full">
        <div className="flex items-center justify-between mb-0 border-b-2 border-[#DFEAF2] pb-2">
          <div className="w-full">
            <div className="flex justify-start items-center gap-2">
              <h2 className="chartHeading w-100 !text-[#34656D]">
                Proposed Generator Schedule
              </h2>
              <span>
                <Edit className="w-6 h-6 text-gray-500" />
              </span>
            </div>
          </div>
          <div className="w-full text-right">
            <div className="flex justify-start items-center gap-2">
              <h2 className="chartHeading w-100 !text-[#34656D]">
                Generation Expense per Generator
              </h2>
              <span>
                <Edit className="w-6 h-6 text-gray-500" />
              </span>
            </div>
          </div>
        </div>
        {loading ? (
          <div className="h-full w-full bg-white flex items-center justify-center">
            <img
              src={loader}
              width={200}
              alt="loader"
              className="block mt-[5rem]"
            />
          </div>
        ) : (
          <div className="flex items-center justify-between h-full">
            <div className="w-full h-full p-2 text-start ">
              <div className="space-y-3 w-full h-full overflow-y-auto pr-2 ">
                {filtered
                  .slice()
                  .sort((a, b) => b.quantum - a.quantum)
                  .map((item, index) => {
                    const barWidthPercent =
                      (item.quantum / quantumMaxPrice) * 100;
                    const isBarTooSmall = barWidthPercent < 30;

                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between"
                      >
                        {/* Name */}
                        <span className="text-sm text-[#8C8A96] w-40 truncate">
                          {normalizeDisplayLabel(item.generator)}
                        </span>

                        {/* Bar */}
                        <div className="flex-1 ml-2 relative">
                          <div className="relative rounded-lg h-7 overflow-hidden">
                            <div
                              className={`absolute top-0 left-0 h-7 bg-[#FCE9DE] flex items-center transition-all duration-500 ${
                                isBarTooSmall
                                  ? "justify-start pl-2"
                                  : "justify-end pr-2"
                              }`}
                              style={{ width: `${barWidthPercent}%` }}
                            >
                              {!isBarTooSmall && (
                                <span className="text-[#913A08] font-semibold text-xs">
                                  {item.quantum.toFixed(2)} MWh
                                </span>
                              )}
                            </div>
                          </div>
                          {isBarTooSmall && (
                            <span
                              className="absolute top-0 left-0 h-7 flex items-center text-[#913A08] font-semibold text-xs ml-2"
                              style={{
                                left: `${barWidthPercent}%`,
                                marginLeft: "8px",
                              }}
                            >
                              {item.quantum.toFixed(2)} MWh
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
            <div className="w-full h-full p-2 text-start ">
              <div className="space-y-3 w-full h-full overflow-y-auto pr-2">
                {filtered
                  .slice()
                  .sort((a, b) => b.price - a.price)
                  .map((item, index) => {
                    const barWidthPercent = (item.price / priceMaxPrice) * 100;
                    const isBarTooSmall = barWidthPercent < 30;

                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between"
                      >
                        {/* Name */}
                        <span className="text-sm text-[#8C8A96] w-40 truncate">
                          {normalizeDisplayLabel(item.generator)}
                        </span>
                        {/* Bar */}
                        <div className="flex-1 ml-2 relative">
                          <div className="relative rounded-lg h-7 overflow-hidden">
                            <div
                              className={`absolute top-0 left-0 h-7 bg-[#E3F1F3] flex items-center transition-all duration-500 ${
                                isBarTooSmall
                                  ? "justify-start pl-2"
                                  : "justify-end pr-2"
                              }`}
                              style={{ width: `${barWidthPercent}%` }}
                            >
                              {!isBarTooSmall && (
                                <span className="text-[#34656D] font-semibold text-xs">
                                  ₹ {item.price.toFixed(2)}
                                </span>
                              )}
                            </div>
                          </div>
                          {isBarTooSmall && (
                            <span
                              className="absolute top-0 left-0 h-7 flex items-center text-[#34656D] font-semibold text-xs ml-2"
                              style={{
                                left: `${barWidthPercent}%`,
                                marginLeft: "8px",
                              }}
                            >
                              ₹ {item.price.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
